import { useState, useRef, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import PdfTileLayer from './PdfTileLayer'
import DxfLayer from './DxfLayer'
import { colors } from '../../styles/tokens'

// Package D — READ-ONLY drawing + measurement markup preview.
// Deliberately NOT a wrapper around the full TakeoffCanvas: it carries
// NO Toolbar, NO editing, NO Undo/Redo, NO Supersede, NO calibration
// editing, NO drawing tools, and performs ZERO database mutation. It
// exists purely to show "where did this quantity come from" on the
// actual drawing.
//
// Reused UNCHANGED from TakeoffCanvas.jsx:
//   - PAGE_BASE_WIDTH/HEIGHT (900 / 900*1.414) — the same PDF/world
//     coordinate space every measurement's geometry_data was recorded in.
//   - screenToPdf's transform math (pan/zoom division) — copied here
//     verbatim because this component owns its OWN pan/zoom state
//     (Fit-to-View only, no drawing/editing interaction), not
//     TakeoffCanvas's.
//   - computeDxfBoundingBox — for DXF Fit-to-View, copied verbatim.
//   - MeasurementGeometry rendering logic — re-implemented inline below
//     using the exact same {points/center+radius} shape and stroke
//     rules, since the original is a private (non-exported) function
//     inside TakeoffCanvas.jsx and cannot be imported directly.
const PAGE_BASE_WIDTH = 900
const PAGE_BASE_HEIGHT = PAGE_BASE_WIDTH * 1.414
const MIN_ZOOM = 0.1
const MAX_ZOOM = 20

function computeDxfBoundingBox(entities) {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  for (const e of entities) {
    if (e.points) {
      for (const p of e.points) {
        if (p.x < minX) minX = p.x
        if (p.x > maxX) maxX = p.x
        if (p.y < minY) minY = p.y
        if (p.y > maxY) maxY = p.y
      }
    } else if (e.center) {
      if (e.center.x - e.radius < minX) minX = e.center.x - e.radius
      if (e.center.x + e.radius > maxX) maxX = e.center.x + e.radius
      if (e.center.y - e.radius < minY) minY = e.center.y - e.radius
      if (e.center.y + e.radius > maxY) maxY = e.center.y + e.radius
    }
  }
  if (!isFinite(minX)) return null
  return { minX, maxX, minY, maxY }
}

// Renders a single measurement's geometry_data, matching
// TakeoffCanvas's MeasurementGeometry visual rules for the read-only
// case (no selected/hovered states — this preview shows exactly one
// measurement, always emphasized).
function PreviewMeasurementGeometry({ measurement, zoom }) {
  const g = measurement.geometry_data
  if (!g) return null
  const isNegative = measurement.polarity === 'negative'
  const stroke = isNegative ? '#7C3AED' : '#2563EB'
  const strokeWidth = 3 / zoom
  // FIX: the shape to draw is determined by geometry_data.type (the
  // actual stored geometry kind — 'rectangle', 'polyline', 'length',
  // 'count', or absent entirely for a plain polygon), NEVER by
  // measurement.measurement_type. Verified directly against
  // production data: an 'area' measurement's geometry_data.type can
  // be 'rectangle' (two diagonal points) OR null (a genuine
  // multi-point polygon with no explicit type field) — both are
  // 'area' measurements, but require completely different rendering.
  // The previous version branched on measurement_type ('area'), which
  // never matched the 'rectangle' branch below it, so every
  // rectangle-shaped Area measurement silently rendered nothing.
  const geomType = g.type

  if (geomType === 'rectangle') {
    const pts = g.points || []
    if (pts.length < 2) return null
    const x = Math.min(pts[0].x, pts[1].x), y = Math.min(pts[0].y, pts[1].y)
    const w = Math.abs(pts[1].x - pts[0].x), h = Math.abs(pts[1].y - pts[0].y)
    return <rect x={x} y={y} width={w} height={h} fill={isNegative ? 'rgba(124,58,237,0.15)' : 'rgba(37,99,235,0.15)'} stroke={stroke} strokeWidth={strokeWidth} />
  }
  if (geomType === 'polyline' || geomType === 'length') {
    const pts = g.points || []
    if (pts.length < 2) return null
    return <polyline points={pts.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeDasharray={isNegative ? `${6 / zoom} ${4 / zoom}` : 'none'} />
  }
  if (geomType === 'count') {
    const pt = g.point || g.points?.[0]
    if (!pt) return null
    return <circle cx={pt.x} cy={pt.y} r={8 / zoom} fill={stroke} stroke="#fff" strokeWidth={2 / zoom} />
  }
  // Fallback for a plain polygon (geometry_data.type absent/null but
  // points.length >= 3) — matches the 'area' measurement discovered
  // with geom_type=null in production data. Also covers any future
  // dimension/polyline-with-3+-points cases safely by point count
  // rather than an assumed type string.
  const pts = g.points || []
  if (pts.length >= 3) {
    return <polygon points={pts.map((p) => `${p.x},${p.y}`).join(' ')} fill={isNegative ? 'rgba(124,58,237,0.15)' : 'rgba(37,99,235,0.15)'} stroke={stroke} strokeWidth={strokeWidth} />
  }
  if (pts.length === 2) {
    return <polyline points={pts.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
  }
  return null
}

// Props: measurement (the one to highlight — required), sheetId,
// drawingVersionId (both required, resolved by the caller from
// measurement.sheet_id per the approved data flow), onClose.
export default function MeasurementPreview({ measurement, sheetId, drawingVersionId, onClose }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [fileUrl, setFileUrl] = useState(null)
  const [fileType, setFileType] = useState('pdf')
  const [dxfEntities, setDxfEntities] = useState([])
  const containerRef = useRef(null)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [viewportRect, setViewportRect] = useState(null)
  const [isPanning, setIsPanning] = useState(false)
  const panStart = useRef(null)
  const fitDoneRef = useRef(false)

  const screenToPdf = useCallback((clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect()
    return { x: (clientX - rect.left - pan.x) / zoom, y: (clientY - rect.top - pan.y) / zoom }
  }, [pan, zoom])

  // Load the EXACT drawing_version this Sheet belongs to — never a
  // substitute/latest revision. This is the approved data flow:
  // measurement -> sheet_id -> sheet.drawing_version_id ->
  // drawing_version.file_path/file_type -> signed Storage URL.
  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      const { data: dv, error: dvErr } = await supabase
        .from('drawing_version').select('file_path, file_type').eq('id', drawingVersionId).maybeSingle()
      if (cancelled) return
      if (dvErr || !dv?.file_path) { setError(dvErr?.message || 'Drawing file not found for this revision.'); setLoading(false); return }
      const { data: signed, error: signErr } = await supabase.storage.from('drawings').createSignedUrl(dv.file_path, 300)
      if (cancelled) return
      if (signErr) { setError(signErr.message); setLoading(false); return }
      setFileUrl(signed.signedUrl)
      setFileType(dv.file_type || 'pdf')
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [drawingVersionId])

  // DXF parsing — reuses the SAME Worker as TakeoffCanvas, unchanged.
  useEffect(() => {
    if (fileType !== 'dxf' || !fileUrl) { setDxfEntities([]); return }
    let cancelled = false
    const worker = new Worker(new URL('../../lib/dxfParser.worker.js', import.meta.url), { type: 'module' })
    worker.onmessage = (e) => {
      if (cancelled) return
      const result = e.data
      if (result.ok) setDxfEntities(result.entities)
      worker.terminate()
    }
    fetch(fileUrl).then((r) => r.text()).then((text) => { if (!cancelled) worker.postMessage({ fileText: text }) })
    return () => { cancelled = true; worker.terminate() }
  }, [fileType, fileUrl])

  // Fit-to-View — runs ONCE per drawing load, centering on the
  // measurement's own geometry (not the whole page), so the markup is
  // immediately visible and readable regardless of drawing size.
  useEffect(() => {
    if (fitDoneRef.current || !containerRef.current || loading) return
    const g = measurement.geometry_data
    if (!g) return
    let bbox = null
    if (g.points && g.points.length > 0) {
      const xs = g.points.map((p) => p.x), ys = g.points.map((p) => p.y)
      bbox = { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) }
    } else if (g.point) {
      bbox = { minX: g.point.x - 20, maxX: g.point.x + 20, minY: g.point.y - 20, maxY: g.point.y + 20 }
    }
    if (!bbox) return
    const w = containerRef.current.clientWidth
    const h = containerRef.current.clientHeight
    const bboxWidth = Math.max(bbox.maxX - bbox.minX, 1)
    const bboxHeight = Math.max(bbox.maxY - bbox.minY, 1)
    // Pad generously (4x) so the measurement sits in context of its
    // surrounding drawing, not cropped to its own exact bounds.
    const zw = (w - 60) / (bboxWidth * 4)
    const zh = (h - 60) / (bboxHeight * 4)
    const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.min(zw, zh)))
    const centerX = (bbox.minX + bbox.maxX) / 2
    const centerY = (bbox.minY + bbox.maxY) / 2
    setZoom(newZoom)
    setPan({ x: w / 2 - centerX * newZoom, y: h / 2 - centerY * newZoom })
    fitDoneRef.current = true
  }, [loading, measurement])

  // viewportRect — REQUIRED by PdfTileLayer (it renders nothing without
  // it, per its own `if (!viewportRect) return []` guard). Computed via
  // the exact same screenToPdf transform TakeoffCanvas uses.
  useEffect(() => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const topLeft = screenToPdf(rect.left, rect.top)
    const bottomRight = screenToPdf(rect.left + rect.width, rect.top + rect.height)
    setViewportRect({ x: topLeft.x, y: topLeft.y, width: bottomRight.x - topLeft.x, height: bottomRight.y - topLeft.y })
  }, [pan, zoom, screenToPdf])

  function handleWheel(e) {
    e.preventDefault()
    const rect = containerRef.current.getBoundingClientRect()
    const pdfPointBefore = screenToPdf(e.clientX, e.clientY)
    const delta = e.deltaY < 0 ? 1.15 : 1 / 1.15
    const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * delta))
    const screenX = e.clientX - rect.left, screenY = e.clientY - rect.top
    setZoom(newZoom)
    setPan({ x: screenX - pdfPointBefore.x * newZoom, y: screenY - pdfPointBefore.y * newZoom })
  }
  function handleMouseDown(e) { setIsPanning(true); panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y } }
  function handleMouseMove(e) { if (isPanning && panStart.current) setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y }) }
  function handleMouseUp() { setIsPanning(false); panStart.current = null }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 8, width: '90vw', height: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary }}>
            Measurement Preview — {measurement.measurement_type}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: colors.textMuted }}>×</button>
        </div>
        <div
          ref={containerRef}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ flex: 1, overflow: 'hidden', position: 'relative', cursor: isPanning ? 'grabbing' : 'grab', background: '#F1F5F9' }}
        >
          {loading ? (
            <div style={{ padding: 20, color: colors.textMuted, fontSize: 13 }}>Loading drawing...</div>
          ) : error ? (
            <div style={{ padding: 20, color: colors.danger, fontSize: 13 }}>{error}</div>
          ) : (
            <div style={{ position: 'absolute', top: 0, left: 0, transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}>
              {fileType === 'pdf' && fileUrl ? (
                <PdfTileLayer fileUrl={fileUrl} pageIndex={0} pageWidth={PAGE_BASE_WIDTH} pageHeight={PAGE_BASE_HEIGHT} zoom={zoom} viewportRect={viewportRect} />
              ) : fileType === 'dxf' ? (
                <div style={{ width: PAGE_BASE_WIDTH, height: PAGE_BASE_HEIGHT, background: '#fff' }}>
                  <svg width={PAGE_BASE_WIDTH} height={PAGE_BASE_HEIGHT} style={{ position: 'absolute', top: 0, left: 0 }}>
                    <DxfLayer entities={dxfEntities} zoom={zoom} />
                  </svg>
                </div>
              ) : null}
              <svg width={PAGE_BASE_WIDTH} height={PAGE_BASE_HEIGHT} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
                <PreviewMeasurementGeometry measurement={measurement} zoom={zoom} />
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
